<template>
  <v-dialog v-model="visible" fullscreen scrollable persistent transition="dialog-bottom-transition">
    <v-card dark class="content-editor">
      <header class="content-editor-header">
        <span class="header-mark"><v-icon>mdi-web-box</v-icon></span>
        <div class="header-copy">
          <div class="header-eyebrow">Studio Content</div>
          <h1>{{ selectedStudio.name }}</h1>
          <p>Video, Beschreibung und Bildergalerie bearbeiten.</p>
        </div>
        <div class="header-actions">
          <v-btn text class="cancel-action" @click="closeDialog">Abbrechen</v-btn>
          <v-btn color="#009640" depressed class="primary-action" :loading="isSaving" :disabled="!canSave" @click="saveContent"
            ><v-icon left>mdi-content-save-outline</v-icon>Speichern</v-btn
          >
          <v-btn icon class="icon-action" aria-label="Dialog schliessen" @click="closeDialog"><v-icon>mdi-close</v-icon></v-btn>
        </div>
      </header>

      <v-card-text class="content-editor-body">
        <div class="editor-layout">
          <div class="editor-column">
            <section class="editor-section">
              <div class="section-heading">
                <span class="section-icon"><v-icon>mdi-video-outline</v-icon></span>
                <div>
                  <div class="section-eyebrow">Hero-Bereich</div>
                  <h2>Header-Video</h2>
                  <p>Das Video wird prominent auf der Studioseite ausgespielt.</p>
                </div>
              </div>
              <v-responsive v-if="videoPreviewSrc" :aspect-ratio="16 / 9" class="video-frame">
                <video :key="videoPreviewSrc" :src="videoPreviewSrc" autoplay loop muted playsinline @error="onPreviewError">
                  Dein Browser unterstützt das Video-Tag nicht.
                </video>
              </v-responsive>
              <div v-else class="media-placeholder">
                <v-icon>mdi-video-off-outline</v-icon><strong>Noch kein Video</strong><span>Lade ein MP4-Video für den Header hoch.</span>
              </div>

              <p class="video-hint">MP4 (H.264), maximal 60 Sekunden und weniger als 100 MB.</p>
              <v-file-input
                v-model="videoFile"
                :disabled="processingVideo || !contentLoaded || isSaving"
                label="Neues MP4-Video hochladen"
                accept="video/mp4"
                prepend-inner-icon="mdi-video-plus-outline"
                prepend-icon=""
                outlined
                dark
                class="modern-field upload-field"
                hide-details="auto" />
              <div v-if="processingVideo" class="processing-state">
                <v-progress-circular :indeterminate="videoStage !== 'upload'" :value="videoProgress" color="#009640" size="28" width="3" />
                <span>{{ videoStage === "upload" ? `Video wird hochgeladen: ${videoProgress} %` : "Video wird geprüft und vorbereitet …" }}</span>
              </div>
              <div v-if="videoJob && !processingVideo" class="video-retry">
                <v-btn text color="#58d388" @click="uploadVideo(true)">Erneut versuchen</v-btn>
                <v-btn text @click="discardVideoUpload">Upload verwerfen</v-btn>
              </div>
            </section>

            <section class="editor-section">
              <div class="section-heading">
                <span class="section-icon"><v-icon>mdi-text-box-outline</v-icon></span>
                <div>
                  <div class="section-eyebrow">Clubprofil</div>
                  <h2>Beschreibung</h2>
                  <p>Ein klarer Einstiegstext für Interessierte und Mitglieder.</p>
                </div>
              </div>
              <v-textarea
                v-model="studioContent.clubSection.text"
                label="Club Beschreibungstext"
                outlined
                dark
                auto-grow
                rows="8"
                placeholder="Willkommen im well come FIT..."
                class="modern-field description-field"
                hide-details="auto" />
            </section>
          </div>

          <section class="editor-section gallery-section">
            <div class="section-heading">
              <span class="section-icon"><v-icon>mdi-image-multiple-outline</v-icon></span>
              <div>
                <div class="section-eyebrow">Medien</div>
                <h2>Bildergalerie</h2>
                <p>Neue Bilder hochladen, beschriften und per Drag &amp; Drop sortieren.</p>
              </div>
            </div>

            <div class="upload-zone">
              <v-file-input
                v-model="imageFiles"
                label="Mehrere Bilder auswählen"
                multiple
                accept="image/*"
                prepend-inner-icon="mdi-image-plus-outline"
                prepend-icon=""
                outlined
                dark
                class="modern-field"
                hide-details="auto"
                @change="onFilesSelected" />

              <div v-if="uploadItems.length" class="upload-preview-list">
                <div v-for="(item, index) in uploadItems" :key="index" class="upload-preview-row">
                  <v-img :src="item.preview" aspect-ratio="1" width="64" height="64" class="preview-image" />
                  <v-text-field v-model="item.alt" label="Alt-Text für SEO" outlined dense dark hide-details class="modern-field preview-alt" />
                  <v-btn icon class="remove-action" :aria-label="`Bild ${index + 1} entfernen`" @click="removeUploadItem(index)"
                    ><v-icon>mdi-close</v-icon></v-btn
                  >
                </div>
                <v-btn
                  color="#009640"
                  depressed
                  block
                  class="primary-action upload-action"
                  :loading="isUploadingImages"
                  :disabled="areUploadItemsInvalid"
                  @click="uploadImages">
                  <v-icon left>mdi-cloud-upload-outline</v-icon>{{ uploadItems.length }} Bild(er) hochladen
                </v-btn>
              </div>
            </div>

            <draggable v-model="studioContent.clubSection.pictures" handle=".drag-handle" ghost-class="ghost" class="gallery-list">
              <transition-group type="transition" name="flip-list">
                <article v-for="(picture, index) in studioContent.clubSection.pictures" :key="picture.src" class="gallery-row">
                  <v-btn icon class="drag-handle" :aria-label="`Bild ${index + 1} verschieben`"><v-icon>mdi-drag-horizontal</v-icon></v-btn>
                  <v-img
                    :src="`${linkPreview}/api/website/image/${picture.src}`"
                    :alt="picture.alt"
                    aspect-ratio="1"
                    width="76"
                    height="76"
                    class="gallery-image" />
                  <v-text-field v-model="picture.alt" label="Alt-Text" outlined dense dark hide-details class="modern-field gallery-alt" />
                  <v-btn icon class="delete-action" :aria-label="`Bild ${index + 1} löschen`" @click="removePicture(index)"
                    ><v-icon>mdi-delete-outline</v-icon></v-btn
                  >
                </article>
              </transition-group>
            </draggable>

            <div v-if="!studioContent.clubSection.pictures || !studioContent.clubSection.pictures.length" class="gallery-empty">
              <v-icon>mdi-image-off-outline</v-icon><strong>Galerie ist leer</strong><span>Lade oben die ersten Studiobilder hoch.</span>
            </div>
          </section>
        </div>
      </v-card-text>

      <footer class="mobile-save-bar">
        <v-btn text class="cancel-action" @click="closeDialog">Abbrechen</v-btn
        ><v-btn color="#009640" depressed class="primary-action" :loading="isSaving" :disabled="!canSave" @click="saveContent">Speichern</v-btn>
      </footer>
    </v-card>
  </v-dialog>
</template>

<script>
import api from "@/api"
import draggable from "vuedraggable"
import { uploadR2File } from "@/services/r2UploadService"
import { readWebsiteVideo } from "@/services/websiteVideoUploadService"

export default {
  components: { draggable },
  props: {
    selectedStudio: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      visible: true,
      studioContent: { clubSection: { text: "", pictures: [] } },
      videoFile: null,
      imageFiles: [],
      uploadItems: [],
      videoPreviewSrc: "",
      videoFallbackSrc: "",
      videoToken: null,
      videoJob: null,
      videoStage: "",
      videoProgress: 0,
      videoController: null,
      videoRequest: 0,
      contentRequest: 0,
      contentLoaded: false,
      processingVideo: false,
      isUploadingImages: false,
      isSaving: false,
    }
  },
  computed: {
    canSave() {
      return this.contentLoaded && !this.processingVideo && !this.videoJob && !this.isUploadingImages && !this.isSaving
    },
    linkPreview() {
      return window.location.href.includes("local") ? "https://app.wellcomefit.ch" : ""
    },
    areUploadItemsInvalid() {
      return this.uploadItems.length === 0
    },
  },
  watch: {
    selectedStudio: {
      immediate: true,
      handler() {
        this.loadContent()
      },
    },
    videoFile(newFile) {
      if (newFile) this.uploadVideo()
    },
  },
  beforeDestroy() {
    this.discardVideoUpload()
    this.contentRequest += 1
    this.uploadItems.forEach((item) => URL.revokeObjectURL(item.preview))
  },
  methods: {
    closeDialog() {
      if (this.isSaving) return
      this.discardVideoUpload()
      this.visible = false
      this.$emit("close-dialog")
    },
    async loadContent() {
      const request = ++this.contentRequest
      this.discardVideoUpload()
      this.contentLoaded = false
      this.videoToken = null
      this.videoPreviewSrc = ""
      try {
        const { result } = await api.fetchWebsiteContent(this.selectedStudio.code)
        if (request !== this.contentRequest) return
        if (result) {
          this.studioContent = result
          if (!this.studioContent.clubSection) this.$set(this.studioContent, "clubSection", { text: "", pictures: [] })
          if (!this.studioContent.clubSection.pictures) this.$set(this.studioContent.clubSection, "pictures", [])
        } else {
          this.studioContent = this.initNewContent()
        }
        this.contentLoaded = true
        await this.loadPreview(this.studioContent.headerVideo, request)
      } catch (e) {
        this.$toast.error("Fehler beim Laden des Inhalts")
      }
    },
    initNewContent() {
      return { studio: this.selectedStudio._id, headerVideo: null, clubSection: { text: "", pictures: [] } }
    },
    async saveContent() {
      if (!this.canSave) return
      this.isSaving = true
      try {
        await api.updateWebsiteContent({ ...this.studioContent, headerVideoToken: this.videoToken })
        this.$toast.success("Inhalt erfolgreich gespeichert!")
        this.isSaving = false
        this.closeDialog()
      } catch (e) {
        this.$toast.error(e.response?.data?.error || "Fehler beim Speichern des Inhalts.")
      } finally {
        this.isSaving = false
      }
    },
    previewUrl(value) {
      return value && value.startsWith("/api/") ? this.linkPreview + value : value
    },
    async loadPreview(file, request) {
      if (!file) return
      const videoRequest = this.videoRequest
      try {
        const result = await api.fetchWebsiteVideoSources(file)
        if (request !== this.contentRequest || videoRequest !== this.videoRequest) return
        this.videoPreviewSrc = this.previewUrl(result.sources.large)
        this.videoFallbackSrc = this.previewUrl(result.fallbackSources.large)
      } catch (_) {
        if (request !== this.contentRequest || videoRequest !== this.videoRequest) return
        this.videoPreviewSrc = `${this.linkPreview}/api/website/video/${file.replace(/\.mp4$/, "")}-large.mp4`
        this.videoFallbackSrc = ""
      }
    },
    onPreviewError() {
      if (this.videoFallbackSrc && this.videoFallbackSrc !== this.videoPreviewSrc) {
        this.videoPreviewSrc = this.videoFallbackSrc
        this.videoFallbackSrc = ""
      } else {
        this.videoPreviewSrc = ""
        this.$toast.error("Die Videovorschau ist momentan nicht verfügbar.")
      }
    },
    discardVideoUpload() {
      this.videoRequest += 1
      if (this.videoController) this.videoController.abort()
      this.videoController = null
      this.videoJob = null
      this.videoFile = null
      this.processingVideo = false
    },
    async uploadVideo(retry = false) {
      if (this.processingVideo || !this.contentLoaded) return
      const file = retry && this.videoJob ? this.videoJob.file : this.videoFile
      if (!file) return
      const request = ++this.videoRequest
      this.processingVideo = true
      this.videoStage = "prepare"
      this.videoProgress = 0
      this.videoController = new AbortController()
      const signal = this.videoController.signal
      if (!retry || (this.videoJob && (Date.now() >= this.videoJob.session.expiresAt ||
          (!this.videoJob.uploaded && Date.now() >= this.videoJob.session.uploadExpiresAt)))) this.videoJob = null
      try {
        if (!this.videoJob) {
          const metadata = await readWebsiteVideo(file)
          if (request !== this.videoRequest) return
          const session = await api.prepareWebsiteVideo({ ...metadata, studio: this.selectedStudio._id }, signal)
          if (request !== this.videoRequest) return
          this.videoJob = { file, session, uploaded: false }
        }
        const job = this.videoJob
        if (!job.uploaded) {
          this.videoStage = "upload"
          await uploadR2File(job.file, job.session, { signal, onProgress: (loaded, total) => {
            if (request === this.videoRequest) this.videoProgress = Math.round(100 * loaded / total)
          } })
          if (request !== this.videoRequest) return
          job.uploaded = true
        }
        this.videoStage = "verify"
        const response = await api.completeWebsiteVideo({ studio: this.selectedStudio._id, sessionToken: job.session.sessionToken }, signal)
        if (request !== this.videoRequest) return
        this.$set(this.studioContent, "headerVideo", response.file)
        this.videoToken = response.videoToken
        this.videoPreviewSrc = response.sources.large
        this.videoFallbackSrc = response.fallbackSources.large
        this.videoJob = null
        this.videoFile = null
        this.$toast.success("Video ist bereit. Mit Speichern wird es veröffentlicht.")
      } catch (error) {
        if (request === this.videoRequest) this.$toast.error(error.response?.data?.error || error.message || "Fehler beim Hochladen des Videos")
      } finally {
        if (request === this.videoRequest) {
          this.processingVideo = false
          this.videoController = null
          if (!this.videoJob) this.videoFile = null
        }
      }
    },
    onFilesSelected() {
      this.uploadItems.forEach((item) => URL.revokeObjectURL(item.preview))
      this.uploadItems = (this.imageFiles || []).map((file) => ({ file, alt: "", preview: URL.createObjectURL(file) }))
    },
    removeUploadItem(index) {
      const item = this.uploadItems[index]
      if (item) URL.revokeObjectURL(item.preview)
      this.uploadItems.splice(index, 1)
      this.imageFiles.splice(index, 1)
    },
    async uploadImages() {
      this.isUploadingImages = true
      for (const item of this.uploadItems) {
        const formData = new FormData()
        formData.append("image", item.file)
        formData.append("alt", item.alt)
        try {
          const response = await api.uploadWebsiteImage(formData)
          if (response.file) this.studioContent.clubSection.pictures.unshift({ src: response.file, alt: response.alt })
        } catch (error) {
          this.$toast.error(`Fehler beim Hochladen von: ${item.file.name}`)
        }
      }
      this.$toast.success(`${this.uploadItems.length} Bild(er) erfolgreich hochgeladen`)
      this.uploadItems.forEach((item) => URL.revokeObjectURL(item.preview))
      this.uploadItems = []
      this.imageFiles = []
      this.isUploadingImages = false
    },
    removePicture(index) {
      this.studioContent.clubSection.pictures.splice(index, 1)
    },
  },
}
</script>

<style scoped>
.video-hint { font-size: 0.875rem; line-height: 1.5; color: #d4d4d8; margin: 1rem 0; }
.video-retry { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 0.75rem; }
.content-editor {
  --border: rgba(255, 255, 255, 0.1);
  --text: #f4f4f5;
  --text-soft: #d4d4d8;
  --muted: #a3a3a8;
  display: flex;
  width: 100%;
  height: 100vh;
  height: 100dvh;
  min-height: 0;
  flex-direction: column;
  overflow: hidden;
  color: var(--text);
  background: #161617 !important;
  font-size: 1rem;
}

.content-editor-header {
  position: sticky;
  z-index: 5;
  top: 0;
  display: flex;
  min-height: 6rem;
  flex: 0 0 auto;
  align-items: center;
  gap: 0.875rem;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border);
  background: linear-gradient(115deg, rgba(0, 150, 64, 0.07), transparent 48%), rgba(24, 24, 25, 0.97);
  backdrop-filter: blur(1rem);
}

.header-mark,
.section-icon {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
}

.header-mark {
  width: 3rem;
  height: 3rem;
  border: 1px solid rgba(0, 150, 64, 0.26);
  border-radius: 0.875rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.header-mark .v-icon,
.section-icon .v-icon {
  color: currentColor;
}

.header-copy {
  min-width: 0;
}

.header-eyebrow,
.section-eyebrow {
  color: #58d388;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.header-copy h1 {
  margin: 0.125rem 0 0;
  color: #ffffff;
  font-size: 1.375rem;
  line-height: 1.2;
}

.header-copy p,
.section-heading p {
  margin: 0.1875rem 0 0;
  color: var(--muted);
  font-size: 0.875rem;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
}

.primary-action,
.cancel-action {
  min-height: 2.75rem !important;
  border-radius: 0.8125rem !important;
  font-size: 0.875rem !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.primary-action {
  color: #ffffff !important;
}

.cancel-action {
  color: #ffffff !important;
}

.icon-action {
  width: 2.75rem !important;
  min-width: 2.75rem !important;
  height: 2.75rem !important;
  border: 1px solid var(--border);
  border-radius: 0.8125rem !important;
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.055) !important;
}

.content-editor-body {
  min-height: 0;
  flex: 1 1 auto;
  padding: 1.25rem !important;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.editor-layout {
  display: grid;
  max-width: 92.5rem;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
  gap: 1rem;
  margin: 0 auto;
}

.editor-column {
  display: grid;
  align-content: start;
  gap: 1rem;
}

.editor-section {
  min-width: 0;
  padding: 1.125rem;
  border: 1px solid var(--border);
  border-radius: 1.25rem;
  background: rgba(255, 255, 255, 0.03);
  box-shadow: 0 1rem 2.75rem rgba(0, 0, 0, 0.1);
}

.section-heading {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.section-icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.75rem;
  color: #58d388;
  background: rgba(0, 150, 64, 0.11);
}

.section-heading h2 {
  margin: 0.125rem 0 0;
  color: var(--text);
  font-size: 1.125rem;
}

.video-frame {
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 1rem;
  background: #000000;
}

.video-frame video {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.media-placeholder,
.gallery-empty {
  display: flex;
  min-height: 12rem;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 0.4375rem;
  border: 1px dashed rgba(255, 255, 255, 0.14);
  border-radius: 1rem;
  color: var(--muted);
  background: rgba(0, 0, 0, 0.12);
  text-align: center;
}

.media-placeholder .v-icon,
.gallery-empty .v-icon {
  color: #77797f;
  font-size: 2.75rem;
}

.media-placeholder strong,
.gallery-empty strong {
  color: var(--text-soft);
  font-size: 1rem;
}

.media-placeholder span,
.gallery-empty span {
  font-size: 0.875rem;
}

.modern-field ::v-deep .v-input__slot {
  border-radius: 0.8125rem !important;
  background: rgba(255, 255, 255, 0.035) !important;
}

.modern-field ::v-deep fieldset {
  border-color: rgba(255, 255, 255, 0.15) !important;
}

.modern-field ::v-deep .v-label {
  color: #b8b8bc !important;
  font-size: 0.875rem;
}

.upload-field {
  margin-top: 1rem;
}

.processing-state {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.75rem;
  padding: 0.75rem;
  border-radius: 0.8125rem;
  color: var(--muted);
  background: rgba(0, 150, 64, 0.08);
  font-size: 0.875rem;
}

.upload-zone {
  padding: 1rem;
  border: 1px dashed rgba(0, 150, 64, 0.3);
  border-radius: 1rem;
  background: rgba(0, 150, 64, 0.045);
}

.upload-preview-list,
.gallery-list {
  display: grid;
  gap: 0.625rem;
}

.upload-preview-list {
  margin-top: 0.75rem;
}

.upload-preview-row,
.gallery-row {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem;
  border: 1px solid var(--border);
  border-radius: 0.875rem;
  background: rgba(0, 0, 0, 0.14);
}

.preview-image,
.gallery-image {
  flex: 0 0 auto;
  overflow: hidden;
  border-radius: 0.6875rem;
}

.preview-alt,
.gallery-alt {
  min-width: 0;
  margin: 0;
}

.remove-action,
.delete-action {
  flex: 0 0 auto;
  color: #f87171 !important;
}

.upload-action {
  margin-top: 0.25rem;
}

.gallery-list {
  margin-top: 1rem;
}

.gallery-row {
  min-height: 6rem;
}

.drag-handle {
  flex: 0 0 auto;
  cursor: grab;
  color: #a3a3a8 !important;
}

.ghost {
  opacity: 0.55;
  background: rgba(0, 150, 64, 0.12);
}

.flip-list-move {
  transition: transform 0.3s;
}

.gallery-empty {
  min-height: 10rem;
  margin-top: 1rem;
}

.mobile-save-bar {
  display: none;
}

@media (max-width: 59.9375rem) {
  .editor-layout {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 37.4375rem) {
  .content-editor-header {
    min-height: 5rem;
    padding: 0.75rem;
  }

  .header-mark,
  .header-copy p,
  .header-actions .cancel-action,
  .header-actions .primary-action {
    display: none;
  }

  .header-copy h1 {
    font-size: 1.125rem;
  }

  .content-editor-body {
    padding: 0.75rem 0.75rem 5.5rem !important;
  }

  .editor-layout,
  .editor-column {
    gap: 0.75rem;
  }

  .editor-section {
    padding: 0.875rem;
    border-radius: 1rem;
  }

  .upload-preview-row,
  .gallery-row {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .preview-alt,
  .gallery-alt {
    flex: 1 1 calc(100% - 6rem);
  }

  .mobile-save-bar {
    position: fixed;
    z-index: 6;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    gap: 0.5rem;
    padding: 0.75rem 0.75rem max(0.75rem, env(safe-area-inset-bottom));
    border-top: 1px solid var(--border);
    background: rgba(24, 24, 25, 0.97);
    backdrop-filter: blur(1rem);
  }

  .mobile-save-bar .v-btn {
    flex: 1 1 50%;
  }
}
</style>
